import { useEffect, useState } from 'react';
import { Icon } from '../Icon';
import { shiftWeek, shortWeek, weekSpan } from './boardModel';

export interface BoardFilterValue {
  /** '' = the current week, 'all', or a week key. */
  week: string;
  assignee: string;
  label: string;
  q: string;
}

/**
 * Week picker ("This week" / "All" + previous / next week), assignee, label and text filters.
 * The week key is computed here only to label and step the picker; which cards fall in a week
 * is the server's call (vn_week_key in SQL).
 */
export function BoardFilters({
  value,
  onChange,
  currentWeek,
  me,
  assignees,
  labels,
  labelsFull,
  onToggleLabels,
}: {
  value: BoardFilterValue;
  onChange: (patch: Partial<BoardFilterValue>) => void;
  currentWeek: string;
  me: string | null;
  assignees: string[];
  labels: string[];
  /** Cards show named label chips (true) or compact coloured bars (false). */
  labelsFull: boolean;
  onToggleLabels: () => void;
}) {
  const all = value.week === 'all';
  const week = !value.week || all ? currentWeek : value.week;
  const [text, setText] = useState(value.q);
  useEffect(() => setText(value.q), [value.q]);
  useEffect(() => {
    if (text === value.q) return;
    const t = window.setTimeout(() => onChange({ q: text }), 300);
    return () => window.clearTimeout(t);
  }, [text, value.q, onChange]);

  const goWeek = (k: string) => onChange({ week: k === currentWeek ? '' : k });
  const people = [...new Set([...(me ? [me] : []), ...assignees, ...(value.assignee ? [value.assignee] : [])])];
  const labelOpts = [...new Set([...labels, ...(value.label ? [value.label] : [])])];

  return (
    <div className="board-filters" data-testid="board-filters">
      <div className="board-week" role="group" aria-label="Week">
        <div className="view-toggle">
          <button
            type="button"
            className={!all ? 'active' : ''}
            aria-pressed={!all}
            onClick={() => onChange({ week: '' })}
            data-testid="board-week-this"
          >
            Week
          </button>
          <button
            type="button"
            className={all ? 'active' : ''}
            aria-pressed={all}
            onClick={() => onChange({ week: 'all' })}
            data-testid="board-week-all"
          >
            All
          </button>
        </div>
        {/* The week steppers mean nothing in All (a rolling 30 days), so they are not shown. */}
        {!all && (
          <button
            type="button"
            className="mini-btn mini-btn--icon board-week__step"
            onClick={() => goWeek(shiftWeek(week, -1))}
            aria-label="Previous week"
            title="Previous week"
            data-testid="board-week-prev"
          >
            ‹
          </button>
        )}
        <span className="board-week__label" data-testid="board-week-label" aria-live="polite">
          {all ? (
            'Last 30 days'
          ) : (
            <>
              <strong>{shortWeek(week)}</strong>
              <span className="muted"> {weekSpan(week)}</span>
              {week === currentWeek && <span className="board-week__now">this week</span>}
            </>
          )}
        </span>
        {!all && (
          <button
            type="button"
            className="mini-btn mini-btn--icon board-week__step"
            disabled={week >= currentWeek}
            onClick={() => goWeek(shiftWeek(week, 1))}
            aria-label="Next week"
            title="Next week"
            data-testid="board-week-next"
          >
            ›
          </button>
        )}
        <button
          type="button"
          className={`board-labels-toggle${labelsFull ? ' is-on' : ''}`}
          aria-pressed={labelsFull}
          onClick={onToggleLabels}
          title={labelsFull ? 'Show labels as colour bars' : 'Show label names on cards'}
          data-testid="board-labels-toggle"
        >
          <span className="board-labels-toggle__glyph" aria-hidden="true" />
          Label names
        </button>
      </div>
      <div className="board-filters__fields">
        <label className="board-field">
          <span className="board-field__label">Assignee</span>
          <select
            className="class-select"
            value={value.assignee}
            onChange={(e) => onChange({ assignee: e.target.value })}
            data-testid="board-filter-assignee"
          >
            <option value="">Anyone</option>
            {people.map((p) => (
              <option key={p} value={p}>
                {p === me ? `Me (${p})` : p}
              </option>
            ))}
          </select>
        </label>
        <label className="board-field">
          <span className="board-field__label">Label</span>
          <select
            className="class-select"
            value={value.label}
            onChange={(e) => onChange({ label: e.target.value })}
            data-testid="board-filter-label"
          >
            <option value="">Any label</option>
            {labelOpts.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="board-field board-field--grow">
          <span className="board-field__label">Search</span>
          <span className="board-search">
            <Icon name="search" size={14} className="board-search__icon" />
            <input
              type="search"
              className="board-search__input"
              placeholder="Title, assignee or #id"
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="board-filter-q"
            />
          </span>
        </label>
        {(value.assignee || value.label || value.q) && (
          <button
            type="button"
            className="btn btn-ghost btn-sm board-filters__clear"
            onClick={() => {
              setText('');
              onChange({ assignee: '', label: '', q: '' });
            }}
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
